import { useState, useCallback } from 'react';
import { message } from 'antd';
import { d211Api, FD110, FD111, D211CreateRequest, ValidationError } from '../../../services/d211Api';

export function useD211Record() {
  const [record, setRecord] = useState<FD110 | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (id: string): Promise<FD110 | null> => {
    setLoading(true);
    try {
      const result = await d211Api.get(id);
      setRecord(result);
      return result;
    } catch {
      message.error('讀取記錄失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (
    master: Partial<FD110>,
    details: Partial<FD111>[],
    isNew: boolean
  ): Promise<{ success: boolean; errors?: ValidationError[] }> => {
    setSaving(true);
    try {
      const payload: D211CreateRequest = { master, details };
      let result: FD110;
      if (isNew) {
        result = await d211Api.create(payload);
      } else {
        result = await d211Api.update(master.fd110_01!, payload);
      }
      setRecord(result);
      message.success(isNew ? '新增成功' : '修改成功');
      return { success: true };
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status: number; data: { errors?: ValidationError[]; error?: string } } };
      if (axiosErr?.response?.status === 422) {
        const errors = axiosErr.response.data.errors || [];
        const msgs = errors.map((e: ValidationError) => e.message).join('；');
        message.error('驗證失敗：' + msgs);
        return { success: false, errors };
      }
      const errMsg = axiosErr?.response?.data?.error || '儲存失敗';
      message.error(errMsg);
      return { success: false };
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    try {
      await d211Api.delete(id);
      setRecord(null);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, []);

  return { record, loading, saving, load, save, remove, setRecord };
}
